import { useEffect, useState, useRef } from 'react';
import _ from 'lodash';
import {
  ChatApiEndpointFallbackStrategy,
  PopulatedProjectModelConfigs,
} from '@scale/shared/types/chat';
import {
  getDraftProjectModelConfigurationFromHistory,
  getModelConfigurationsByIdAndVersion,
  getActiveProjectModelConfiguration,
} from 'react-scale/lib/dashboard-api/selfserve';
import { replaceTemplateVariables } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/helpers';
import { ProjectModelConfigDTO, PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';
import { PROJECT_MODEL_CONFIG_VIEW_KEY } from 'react-scale/corp/components/QM/taskTemplatesEditor/ChatApiEndpointsBuilder';
import logger from '@scale/frontend-lib/logging';

// Helper function to convert distributed parameters
export const convertDistributedParams = (params: any) => {
  const resultParams: any = {};
  for (const key in params) {
    const value = params[key];
    let processedValue;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively handle nested objects
      if (value.type === 'enum') {
        const { options } = value;
        processedValue = options[Math.floor(Math.random() * options.length)];
      } else if (value.type === 'range') {
        const { min, max, decimals, asString } = value;
        const v = parseFloat((min + Math.random() * (max - min)).toFixed(decimals));
        processedValue = asString ? v.toString() : v;
      } else {
        processedValue = convertDistributedParams(value);
      }
    } else if (Array.isArray(value)) {
      // Recursively handle arrays
      processedValue = value
        .map(item =>
          typeof item === 'object' && item !== null ? convertDistributedParams(item) : item,
        )
        .filter(item => item !== undefined && item !== '');
    } else {
      processedValue = value;
    }
    // Only include if not undefined and not empty string
    if (processedValue !== undefined && processedValue !== '') {
      resultParams[key] = processedValue;
    }
  }
  return resultParams;
};

// Helper to select and process a model config (including fallback and param processing)
async function selectAndProcessModelConfig({
  distributedModels,
  templateVariables,
}: {
  distributedModels: ProjectModelConfigDTO[];
  templateVariables?: Record<string, string>;
}): Promise<PopulatedProjectModelConfigDTO | undefined> {
  const detailedConfigs: PopulatedProjectModelConfigs[] = await Promise.all(
    distributedModels.map(async (config: ProjectModelConfigDTO) => {
      const detailedConfig = await getModelConfigurationsByIdAndVersion(
        config._id,
        config.version ?? 0,
      );

      if (config.fallbackEndpoint) {
        const fallbackConfig = await getModelConfigurationsByIdAndVersion(
          config.fallbackEndpoint._id,
          config.fallbackEndpoint.version ?? 0,
        );
        return {
          _id: detailedConfig._id,
          version: detailedConfig.version ?? 0,
          url: detailedConfig.url,
          params: detailedConfig.params,
          modelEndpointId: detailedConfig._id,
          distribution: config.distribution,
          fallbackStrategy:
            config.fallbackStrategy ?? ChatApiEndpointFallbackStrategy.FALLBACK_ON_ERROR,
          fallbackEndpoint: {
            _id: fallbackConfig._id,
            version: fallbackConfig.version ?? 0,
            url: fallbackConfig.url,
            params: fallbackConfig.params,
            modelEndpointId: fallbackConfig._id,
          },
        };
      }

      return {
        _id: detailedConfig._id,
        version: detailedConfig.version ?? 0,
        url: detailedConfig.url,
        params: detailedConfig.params,
        modelEndpointId: detailedConfig._id,
        distribution: config.distribution,
      };
    }),
  );

  let cumulative = 0;
  const rand = Math.random() * 100;

  for (const config of detailedConfigs) {
    cumulative += config.distribution ?? 0;
    if (rand < cumulative) {
      const newParams = convertDistributedParams(config.params);
      for (const k in newParams) {
        if (typeof newParams[k] === 'string') {
          newParams[k] = replaceTemplateVariables(newParams[k], templateVariables ?? {});
        }
      }

      const fallbackParams = config.fallbackEndpoint
        ? convertDistributedParams(config.fallbackEndpoint.params)
        : undefined;

      if (fallbackParams) {
        for (const k in fallbackParams) {
          if (typeof fallbackParams[k] === 'string') {
            fallbackParams[k] = replaceTemplateVariables(
              fallbackParams[k],
              templateVariables ?? {},
            );
          }
        }
      }

      return {
        ...config,
        params: newParams, // updated params with distributed values
        ...(config.fallbackEndpoint && {
          fallbackEndpoint: {
            ...config.fallbackEndpoint,
            url: config.fallbackEndpoint.url ?? '',
            params: fallbackParams, // updated params with distributed values
          },
        }),
      };
    }
  }
  return undefined;
}

// Hook to populate model config and expose readiness flag
export function useProjectModelConfiguration({
  templateVariables,
  projectModelConfigurationMapping,
  setProjectModelConfigurationMapping,
  projectId,
  isTaskPreview,
}: {
  templateVariables?: Record<string, string>;
  projectModelConfigurationMapping?: Record<string, PopulatedProjectModelConfigDTO>;
  setProjectModelConfigurationMapping?: (
    projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
  ) => void;
  projectId: string | null;
  isTaskPreview?: boolean;
}) {
  const [draftProjectModelConfiguration, setDraftProjectModelConfiguration] = useState<Record<
    string,
    PopulatedProjectModelConfigs[]
  > | null>(null);

  const [activeProjectModelConfigurations, setActiveProjectModelConfigurations] = useState<Record<
    string,
    ProjectModelConfigDTO[]
  > | null>(null);

  const [loadedDraftProjectModelConfiguration, setLoadedDraftProjectModelConfiguration] =
    useState(false);

  const [loadedActiveProjectModelConfigurations, setLoadedActiveProjectModelConfigurations] =
    useState(false);

  useEffect(() => {
    const fetchProjectModelConfigurations = async () => {
      if (projectId) {
        try {
          const projectModelConfiguration = await getActiveProjectModelConfiguration(projectId);
          if (
            draftProjectModelConfiguration?.projectConfigurationMapping &&
            !_.isEmpty(draftProjectModelConfiguration?.projectConfigurationMapping)
          ) {
            setActiveProjectModelConfigurations(
              projectModelConfiguration.projectModelConfiguration,
            );
          }
        } catch {
          logger.warn('Project has no project model configuration');
        }
      }
      setLoadedActiveProjectModelConfigurations(true);
    };

    const fetchDraftProjectModelConfiguration = async () => {
      if (isTaskPreview && projectId) {
        try {
          const draftProjectModelConfiguration = await getDraftProjectModelConfigurationFromHistory(
            projectId,
          );
          if (
            draftProjectModelConfiguration?.projectConfigurationMapping &&
            !_.isEmpty(draftProjectModelConfiguration?.projectConfigurationMapping)
          ) {
            setDraftProjectModelConfiguration(
              draftProjectModelConfiguration.projectConfigurationMapping,
            );
          }
        } catch {
          logger.warn('Project has no draft project model configuration');
        }
      }
      setLoadedDraftProjectModelConfiguration(true);
    };

    fetchProjectModelConfigurations();
    fetchDraftProjectModelConfiguration();
  }, [projectId, isTaskPreview]);

  const [populatedMapping, setPopulatedMapping] = useState<
    Record<string, PopulatedProjectModelConfigDTO>
  >(projectModelConfigurationMapping ?? {});
  const projectModelConfigsLoadedRef = useRef(false);

  useEffect(() => {
    // eslint-disable-next-line complexity
    const populate = async () => {
      // If we already have a mapping and it's not empty, use it
      if (projectModelConfigurationMapping && !_.isEmpty(projectModelConfigurationMapping)) {
        setPopulatedMapping(projectModelConfigurationMapping);
        projectModelConfigsLoadedRef.current = true;
        return;
      }

      if (!loadedDraftProjectModelConfiguration || !loadedActiveProjectModelConfigurations) {
        return;
      }

      // if we're in a task preview, use the draft project model configuration if the user has selected "proposed"
      let useDraft = false;
      let sessionValue = 'active';
      if (isTaskPreview && typeof window !== 'undefined') {
        sessionValue = sessionStorage.getItem(PROJECT_MODEL_CONFIG_VIEW_KEY) || 'active';
        useDraft = sessionValue === 'proposed';
      }

      // check if the selected config exists, otherwise fallback and update sessionStorage
      const hasDraft =
        draftProjectModelConfiguration && Object.keys(draftProjectModelConfiguration).length > 0;
      const hasActive =
        activeProjectModelConfigurations &&
        Object.keys(activeProjectModelConfigurations).length > 0;
      if (isTaskPreview && typeof window !== 'undefined') {
        if (useDraft && !hasDraft && hasActive) {
          useDraft = false;
          sessionStorage.setItem(PROJECT_MODEL_CONFIG_VIEW_KEY, 'active');
        } else if (!useDraft && !hasActive && hasDraft) {
          useDraft = true;
          sessionStorage.setItem(PROJECT_MODEL_CONFIG_VIEW_KEY, 'proposed');
        }
      }

      const modelConfigurationMapping =
        useDraft && draftProjectModelConfiguration
          ? draftProjectModelConfiguration
          : activeProjectModelConfigurations;

      const newMapping: Record<string, PopulatedProjectModelConfigDTO> = {};

      for (const key of Object.keys(modelConfigurationMapping ?? {})) {
        const distributedModels = modelConfigurationMapping?.[key];
        if (!distributedModels) {
          continue;
        }
        const processedConfig = await selectAndProcessModelConfig({
          distributedModels,
          templateVariables,
        });
        if (processedConfig) {
          newMapping[key] = processedConfig;
        }
      }

      setPopulatedMapping(newMapping);
      setProjectModelConfigurationMapping?.(newMapping);
      projectModelConfigsLoadedRef.current = true;
    };

    projectModelConfigsLoadedRef.current = false;
    populate();
  }, [
    templateVariables,
    activeProjectModelConfigurations,
    draftProjectModelConfiguration,
    loadedDraftProjectModelConfiguration,
    loadedActiveProjectModelConfigurations,
    isTaskPreview,
  ]);

  return { projectModelConfigsLoadedRef, populatedMapping };
}
